'use client'

import { Box, Stack } from '@mantine/core'
import { sortBy } from 'es-toolkit'
import { DataTable, DataTableSortStatus } from 'mantine-datatable'
import { memo, useEffect, useState } from 'react'

import IconCaretUpDown from '@/assets/icons/caret-updown.svg'
import IconCaretUpDownDown from '@/assets/icons/caret-updown-down.svg'
import { SectionErrorBoundary } from '@/components/error/boundary'
import { useI18nRouter } from '@/i18n/navigation'
import { CoinCategory } from '@/services/category'

import { defaultColumnProps, useColumns } from './columns/configs'
import EmptyState from './EmptyState'
import styles from './styles.module.css'

interface CategoryTableProps {
  data: CoinCategory[]
  initialSortStatus?: DataTableSortStatus<CoinCategory>
}

export default memo(TableWrapper)

function TableWrapper({ data, initialSortStatus }: CategoryTableProps) {
  return (
    <SectionErrorBoundary>
      <Table data={data} initialSortStatus={initialSortStatus} />
    </SectionErrorBoundary>
  )
}

function Table({ data, initialSortStatus }: CategoryTableProps) {
  const router = useI18nRouter()

  const [categoriesSortStatus, setCategoriesSortStatus] = useState<
    DataTableSortStatus<CoinCategory> | undefined
  >(initialSortStatus)
  const [sortedCategories, setSortedCategories] = useState(data || [])

  const getSortValue = (item: CoinCategory, sortKey: string): number | string => {
    switch (sortKey) {
      case 'market_cap_native':
        return Number(item.market_cap_native?.amount) || 0
      case 'volume_24h_native':
        return Number(item.volume_24h_native?.amount) || 0
      case 'percent_change_24h_native':
        return Number(item.percent_change_24h_native) || 0
      default:
        return item[sortKey as keyof CoinCategory] as string
    }
  }

  useEffect(() => {
    if (!data) return

    const sortKey = categoriesSortStatus?.sortKey
    if (!sortKey) {
      setSortedCategories(data)
    } else {
      const sorted = sortBy(data, [item => getSortValue(item, sortKey)])
      setSortedCategories(categoriesSortStatus.direction === 'desc' ? sorted.reverse() : sorted)
    }
  }, [data, categoriesSortStatus])

  const handleCategoriesSortStatusChange = (newSortStatus: DataTableSortStatus<CoinCategory>) => {
    setCategoriesSortStatus({
      ...newSortStatus,
      direction:
        newSortStatus.sortKey !== categoriesSortStatus?.sortKey ? 'desc' : newSortStatus.direction,
    })
  }

  const columns = useColumns()

  if (sortedCategories.length === 0) {
    return <EmptyState />
  }

  return (
    <Box pos="relative">
      <Stack gap="16px">
        <DataTable
          textSelectionDisabled
          records={sortedCategories}
          columns={columns}
          defaultColumnProps={defaultColumnProps}
          idAccessor="id"
          pinFirstColumn
          /* Sort */
          sortStatus={categoriesSortStatus as DataTableSortStatus<CoinCategory>}
          sortIcons={{
            sorted: <IconCaretUpDownDown width={14} fill="var(--content-primary)" />,
            unsorted: <IconCaretUpDown width={14} fill="var(--content-primary)" />,
          }}
          onSortStatusChange={handleCategoriesSortStatusChange}
          /* Row Click */
          onRowClick={({ record }: { record: CoinCategory }) => {
            if (record.slug) {
              router.push(`/categories/${record.slug}`)
            }
          }}
          /* Styling */
          classNames={styles}
          minHeight="500px"
          backgroundColor="var(--bg-app)"
          withTableBorder={false}
          withColumnBorders={false}
          withRowBorders={false}
          highlightOnHover
          highlightOnHoverColor="var(--bg-surface-secondary)"
        />
      </Stack>
    </Box>
  )
}
